import { nextTick, ref } from "vue";

/**
 * @description Composable to handle IME (Input Method Editor) composition events
 * Prevents double-firing of input events when using Japanese, Chinese, Korean, etc.
 * @returns {Object} Object containing isComposing state and event handlers
 */
export const useIMEComposition = () => {
  const resetKey = ref(0);
  const isComposing = ref(false);

  const isCompositionEndLocked = ref(false);

  /**
   * Handler for composition start event
   */
  const handleCompositionStart = () => {
    isComposing.value = true;
    isCompositionEndLocked.value = false;
  };

  /**
   * Handler for composition end event
   * @param {Function} callback - Optional callback to execute after composition ends
   */
  const handleCompositionEnd = (callback?: (event: CompositionEvent, ...args: any[]) => void) => {
    return (event: CompositionEvent, ...args: any[]) => {
      isComposing.value = false;
      isCompositionEndLocked.value = true;
      // Execute callback with the final composed text
      // Added the timeout to avoid the stale dom value
      setTimeout(() => {
        if (callback) {
          callback(event, ...args);
        }
        nextTick(() => {
          isCompositionEndLocked.value = false;
        });
      }, 10);
    };
  };

  /**
   * Wrapper for input handlers that should be skipped during composition
   * @param {Function} inputHandler - The original input handler function
   * @returns {Function} Wrapped handler that respects composition state
   */
  const withIMECheck = (inputHandler: (event: Event, ...args: any[]) => void) => {
    return (event: Event, ...args: any[]) => {
      if (!isComposing.value && !isCompositionEndLocked.value) {
        inputHandler(event, ...args);
      }
    };
  };

  return {
    isComposing,
    handleCompositionStart,
    handleCompositionEnd,
    withIMECheck,
    resetKey,
  };
};
